// The single music manager. Two persistent "decks" are unlocked during the visitor's first tap
// (phones only allow audio elements that started inside a tap), so every later track can start
// anywhere. At most one deck is heard; the other only during a 3–4 s crossfade.
const LEVEL = 0.9;
const FADE = 1.0; // time constant: ~97% faded after 3.5 s
const LOOP_LEAD = 4; // seconds before a track ends that the gentle loop crossfade begins
const SILENT = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";

class Tracks {
  ctx = null;
  dest = null;
  cur = null;
  want = null;
  loading = false;
  decks = [];
  seq = 0;

  init(ctx, dest) {
    if (this.ctx || !ctx) return;
    this.ctx = ctx;
    this.dest = dest;
    this.decks = [0, 1].map(() => this.deck());
    if (this.want) {
      const w = this.want;
      this.want = null;
      this.play(w);
    }
  }

  deck() {
    const el = new Audio();
    el.crossOrigin = "anonymous";
    el.preload = "auto";
    const g = this.ctx.createGain();
    g.gain.value = 0;
    this.ctx.createMediaElementSource(el).connect(g).connect(this.dest);
    const d = { el, g, url: null, looping: false, drop: 0 };
    el.src = SILENT;
    el.play().catch(() => {});
    el.addEventListener("timeupdate", () => {
      if (d !== this.cur || d.looping || !el.duration || el.duration - el.currentTime > LOOP_LEAD) return;
      d.looping = true;
      this.play(d.url, true);
    });
    el.addEventListener("ended", () => {
      if (d !== this.cur) return;
      el.currentTime = 0;
      el.play().catch(() => {});
    });
    return d;
  }

  preload(url) {
    if (!url || this.pre?.src.endsWith(url)) return;
    const a = new Audio();
    a.preload = "auto";
    a.src = url;
    a.load();
    this.pre = a;
  }

  fadeOut(d, tau = FADE) {
    const now = this.ctx.currentTime;
    d.g.gain.cancelScheduledValues(now);
    d.g.gain.setTargetAtTime(0, now, tau);
    clearTimeout(d.drop);
    d.drop = setTimeout(() => d !== this.cur && d.el.pause(), 4200);
  }

  fadeIn(d) {
    const now = this.ctx.currentTime;
    clearTimeout(d.drop);
    d.g.gain.cancelScheduledValues(now);
    d.g.gain.setTargetAtTime(LEVEL, now, FADE);
  }

  // tau: an optional quicker fade-out (a journey that must begin in silence)
  async play(url, fresh = false, tau) {
    const target = url || null;
    if (!this.ctx) {
      this.want = target;
      return true;
    }
    if (!fresh && this.want === target && (this.cur?.url === target || this.loading || !target)) return true;
    this.want = target;
    const token = ++this.seq;
    if (!target) {
      if (this.cur) this.fadeOut(this.cur, tau);
      this.cur = null;
      return true;
    }
    // coming back to a track that is still fading out: bring it back instead of restarting it
    const back = !fresh && this.decks.find((d) => d.url === target && !d.el.paused);
    if (back) {
      this.loading = false;
      if (this.cur && this.cur !== back) this.fadeOut(this.cur);
      this.cur = back;
      this.fadeIn(back);
      return true;
    }
    const d = this.decks.find((x) => x !== this.cur);
    clearTimeout(d.drop);
    d.g.gain.cancelScheduledValues(this.ctx.currentTime);
    d.g.gain.setValueAtTime(0, this.ctx.currentTime);
    d.el.pause();
    d.url = target;
    d.looping = false;
    d.el.loop = false;
    d.el.src = target;
    this.loading = true;
    const ok = await new Promise((res) => {
      const done = (v) => () => (d.el.removeEventListener("canplay", yes), d.el.removeEventListener("error", no), res(v));
      const yes = done(true);
      const no = done(false);
      d.el.addEventListener("canplay", yes);
      d.el.addEventListener("error", no);
    });
    if (token !== this.seq) return true;
    this.loading = false;
    const fail = () => {
      if (this.cur) this.fadeOut(this.cur);
      this.cur = null;
      d.url = null;
      this.want = null;
      return false;
    };
    if (!ok) return fail();
    try {
      await d.el.play();
    } catch {
      return fail();
    }
    if (token !== this.seq) return true;
    if (this.cur && this.cur !== d) this.fadeOut(this.cur);
    this.cur = d;
    this.fadeIn(d);
    this.startedAt = performance.now();
    return true;
  }

  state() {
    const name = (u) => (u ? decodeURIComponent(u.split("/").pop()) : null);
    return {
      custom: this.cur?.url || null,
      wanted: this.want,
      loading: this.loading,
      playing: !!this.cur && !this.cur.el.paused,
      time: this.cur ? +this.cur.el.currentTime.toFixed(1) : 0,
      sources: this.decks.filter((d) => d.url && !d.el.paused).map((d) => ({ track: name(d.url), current: d === this.cur, gain: +d.g.gain.value.toFixed(3) })),
    };
  }
}

export const tracks = new Tracks();
